<template>
  <div class="container">
    <div class="row">
      <div class="col-right-padding col-12 col-sm-12 col-md-12 col-lg-8 col-xl-8">
        <h1 v-if="user.isRewards" class="title pb-lg-4 mb-5">Join MyBeauty</h1>
        <h1 v-else class="title pb-lg-4 mb-5">Sign up for Harrods Rewards and join MyBeauty</h1>

        <div>
          <p class="label">Name</p>
          <p class="value">{{ user.title }} {{ user.firstName }} {{ user.lastName }}</p>
        </div>

        <div>
          <p class="label">Email Address</p>
          <p class="value">{{ user.email }}</p>
        </div>

        <div v-if="user.isRewards">
          <p class="label">Harrods Rewards Card Number</p>
          <p class="value">{{ user.cardNumber }}</p>
        </div>

        <div v-if="!user.isRewards">
          <h2 class="header pb-3 pt-lg-3 mb-3 mt-3">
            DO YOU HAVE AN EXISTING REWARDS CARD?
          </h2>

          <input type="radio"
                 id="no"
                 :value="no"
                 class="input-btn mt-2 mr-3"
                 v-model="doesUserHaveRewards" />
          <p class="text clickable pb-3 mb-3" @click="noRewardsCard">
            No, I need to join Harrods Rewards
          </p>
          <input type="radio"
                 id="yes"
                 :value="yes"
                 class="input-btn mt-2 mr-3"
                 v-model="doesUserHaveRewards" />
          <p class="text clickable" @click="yesRewardsCard">
            Yes, I already have a Rewards card
          </p>

          <b-collapse v-model="enterRewardsCard">
            <label class="label mb-1">Harrods Rewards card number</label>
            <input class="form-control input-field mb-3 pl-4"
                   type="text"
                   v-model.trim.lazy="rewardsNumber"
                   :class="{
                     'error-box':
                       v$.rewardsNumber.$dirty &&
                       (!v$.rewardsNumber.sixteenDigits || !v$.rewardsNumber.between),
                   }"
                   placeholder="Enter your 16 digit Rewards card number" />
            <div class="error-text" v-if="v$.rewardsNumber.$dirty">
              <p class="mt-1 mb-3 mb-lg-4"
                 v-if="!v$.rewardsNumber.sixteenDigits || !v$.rewardsNumber.between">
                Rewards Number must be a 16 digit number
              </p>
            </div>

            <router-link to="/en-gb/mybeauty/retrieve-card-number" target="_blank">
              <p class="hyperlink">Don’t know your card number?</p>
            </router-link>
          </b-collapse>
        </div>

        <div class="gold-notification my-3">
          <h2 class="header mb-3">FROM TIME TO TIME, WE'D LIKE TO SEND YOU EMAILS ABOUT:</h2>
          <div class="custom-text-communication">
            <p class="text mb-3">
              <ul class="comms-list">
                <li class="comms-list-item">Updates on your MyBeauty and Harrods Rewards membership</li>
                <li class="comms-list-item">Offers and promotions</li>
                <li class="comms-list-item">The latest Harrods and H beauty news</li>
                <li class="comms-list-item">Exciting beauty launches</li>
                <li class="comms-list-item">Exclusive event invitations</li>
              </ul>
            </p>
          </div>
          <input type="checkbox"
                 class="input-btn mt-2 mr-3"
                 v-model="marketingOptIn" />
          <p class="text">
            No, I don't want to hear about MyBeauty
            and Harrods Rewards benefits
          </p>
        </div>

        <div v-if="!user.isRewards" class="custom-text-communication">
          <p class="text pb-lg-4 mb-5 mt-5" style="line-height: 24px;">
            For a more bespoke shopping experience, we may collect additional relevant data about you (which you have made available via third parties). Please contact Customer Services if you wish to opt-out of this.
          </p>
        </div>

        <button v-if="user.isRewards"
                @click="joinMyBeauty"
                type="submit"
                class="btn btn-primary green-btn">
          <b-spinner v-if="showLoader"
                     label="Spinning"
                     class="white-spinner"></b-spinner>
          <div v-if="!showLoader">Join MyBeauty</div>
        </button>

        <button v-else
                @click="joinMyBeautyAndRewards"
                type="submit"
                class="btn btn-primary green-btn">
          <b-spinner v-if="showLoader"
                     label="Spinning"
                     class="white-spinner"></b-spinner>
          <div v-if="!showLoader">Join Harrods Rewards &amp; MyBeauty</div>
        </button>

        <AgreementUnsubscription class="custom-text-agreement-and-unsubscription" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import useVuelidate from '@vuelidate/core'
import { between } from '@vuelidate/validators'
import AgreementUnsubscription from "@/components/AgreementUnsubscription.vue"
import digitalApi from '@/services/DigitalApiClient.js'

const props = defineProps({
  user: { type: Object, required: true }
})

const showLoader = ref(false)
const marketingOptIn = ref(false)
const doesUserHaveRewards = ref('no')
const yes = 'yes'
const no = 'no'
const rewardsNumber = ref('')

// Vuelidate rules
const rules = computed(() => ({
  rewardsNumber: {
    sixteenDigits: value => value === "" || value.length === 16,
    between: value => between(7042776310000000, 7042776999999999)(Number(value))
  }
}))

const v$ = useVuelidate(rules, { rewardsNumber })

const enterRewardsCard = computed({
  get: () => doesUserHaveRewards.value === yes,
  set: val => { doesUserHaveRewards.value = val ? yes : no }
})

async function joinMyBeauty() {
  showLoader.value = true
  await digitalApi.joinMyBeauty(props.user.id, marketingOptIn.value)
  showLoader.value = false
  // Use useRouter() for Vue 3, or inject $router if needed
  // This assumes you have access to $router in your setup, otherwise use useRouter()
  // Example: const router = useRouter(); router.push({ name: 'DownloadAfterSignIn' })
  // For now, fallback to window.location if router is not available:
  window.location.assign('/download-after-sign-in')
}

async function joinMyBeautyAndRewards() {
  showLoader.value = true
  await digitalApi.upgradeUser(props.user, rewardsNumber.value, 'CN', true, marketingOptIn.value)
  showLoader.value = false
  window.location.assign('/download-after-sign-in')
}

function noRewardsCard() {
  doesUserHaveRewards.value = no
}
function yesRewardsCard() {
  doesUserHaveRewards.value = yes
}
</script>

<style lang="scss" scoped>
.gold-notification {
  color: #424242;
  font-size: 16px;
  background-color: rgba(171, 142, 102, 0.05);
  border: solid 1px #ab8e66;
  border-radius: 0px;
  padding: 15px;

  .header {
    letter-spacing: 1.5px;
  }
}

.custom-text-communication p {
  @media screen and (min-width: 768px) {
    font-size: 15px;
  }
}
.custom-text-agreement-and-unsubscription p {
  font-size: 14px;
  font-weight: normal;
}

.container {
  background-color: $harrods-white;

  @media screen and (max-width: 1199px) and (min-width: 992px) {
    padding-right: 50px;
    padding-left: 50px;
  }

  @media screen and (min-width: 1200px) {
    padding-right: 75px;
    padding-left: 75px;
  }
}

.col-right-padding {
  @media screen and (max-width: 1199px) and (min-width: 992px) {
    padding-right: 148px;
  }

  @media screen and (min-width: 1200px) {
    padding-right: 105px;
  }
}

.input-btn {
  float: left;

  &:hover,
  :active,
  :focus {
    cursor: pointer;
  }
}

.clickable {
  &:hover,
  :active,
  :focus {
    cursor: pointer;
  }
}

.notification {
  height: 802px;
  padding: 30px 30px 50px 30px;

  @media screen and (max-width: 1199px) and (min-width: 992px) {
    width: 302px;
  }

  @media screen and (min-width: 1200px) {
    width: 320px;
    margin-left: 40px;
  }
}

h3 {
  color: $dark-grey-1;
  font-family: "Miller Banner";
  font-size: 24px;
  font-weight: 300;
}

.label {
  font-size: 14px;
  color: #757575;
  display: block;
  padding-bottom: 5px;
  font-weight: normal;
}

.value {
  font-size: 16px;
  font-weight: normal;
  line-height: 1.5;
  letter-spacing: 0.8px;
  color: #424242;
  padding-bottom: 20px;
}
	.comms-list {
		margin-left: 15px;
	}

	.comms-list-item {
		list-style: none;
		line-height: 24px;
		display: flex;
	}

	.comms-list-item:before {
		content: "•";
		padding-right: 20px;
	}
</style>
